<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { Unplug } from 'lucide-vue-next';
import { buttonVariants } from '@/components/ui/button';
import { AREA_ICONS } from '@/navigation/navIcons';
import { cn } from '@/utils/utils';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LazyTooltip } from '@/components/ui/tooltip';
import SyncZoneRow from '@/components/Sync/SyncZoneRow.vue';
import {
    usePendingChangesFeed,
    useRowPlayerIds,
    useZoneDeliveryStatuses,
    type SyncScope,
} from '@/composables/api/useSchedulerSyncApi';
import { zoneSummaryFor } from '@/composables/api/useZoneSummaries';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { useShowInSearch } from '@/composables/useShowInSearch';
import { useSyncScope } from '@/composables/useSyncScope';
import {
    countPlayersBehind,
    isZoneBehind,
    syncPopoverPlacement,
    SYNC_POPOVER_PREFERRED_WIDTH,
    syncPillLabel,
    syncPillPresentation,
    syncPillState,
    syncPopoverSummary,
    groupChangesByZone,
    groupZonesByLocation,
    rowPlayerIds,
    zoneIdsNeedingRowPlayers,
    type PendingChange,
    type SyncPillForm,
    type SyncPopoverPlacement,
    type ZoneLocation,
} from '@/utils/syncStatus';

/**
 * The search toolbar's "Sync" pill and its popover: the zones with content
 * changes that have not reached their players yet, grouped by location.
 *
 * Mine / Everyone is one shared, remembered selection, so the pill counts
 * exactly the zones the popover lists.
 */
const props = withDefaults(
    defineProps<{
        /**
         * "Sync 7 · 5", or the icon alone, as the toolbar's fit state says. Without
         * the counts a red dot on the icon says something is not sent, and the
         * counts stay in the tooltip.
         */
        form?: SyncPillForm;
    }>(),
    { form: 'full' }
);

const isOpen = ref(false);
const scope = useSyncScope();

/**
 * The same flat button as the toolbar's Filter, Display and Export: the toolbar
 * variant at size sm, h-8 and px-2, muted text that lights up on hover. Its
 * corners come from the same variants, so they always match Filter's.
 */
const PILL_CLASS = cn(buttonVariants({ variant: 'toolbar', size: 'sm' }), 'gap-1.5 px-2 has-[>svg]:px-2 font-normal tabular-nums');


const feed = usePendingChangesFeed(scope);
const changes = computed<PendingChange[]>(() => feed.data.value?.pages.flatMap((page) => page.data) ?? []);

const zones = computed(() => groupChangesByZone(changes.value));

// Status for every zone in the feed, popover open or not: the pill counts zones
// behind from it. The same query (same key) feeds the popover, so opening it
// does not ask again, and it refreshes on the feed's 60-second interval.
const zoneIds = computed(() => zones.value.map((zone) => zone.zoneId));
const statusQuery = useZoneDeliveryStatuses(zoneIds);

const statusByZoneId = computed(() => statusQuery.data.value ?? new Map());

const pillState = computed(() =>
    syncPillState({
        feedLoaded: feed.data.value !== undefined,
        feedFailed: feed.isError.value,
        statusLoaded: statusQuery.data.value !== undefined,
        statusFailed: statusQuery.isError.value,
        noZonesToAsk: zoneIds.value.length === 0,
        counts: countPlayersBehind(changes.value, statusByZoneId.value),
    })
);
const pillLabel = computed(() => syncPillLabel(pillState.value));
const pillPresentation = computed(() => syncPillPresentation(pillState.value, props.form));
const showWord = computed(() => pillPresentation.value.showWord);
const showCounts = computed(() => pillPresentation.value.showCounts);
const showProblemDot = computed(() => pillPresentation.value.showProblemDot);

// The icon strip's Players icon, from the shared icon module, so it follows any redesign there.
const playersIcon = AREA_ICONS.players;

/** Retry whatever failed. The pill's "Click to retry" and the popover's Retry both come here. */
function retry(): void {
    if (feed.isError.value) void feed.refetch();
    if (statusQuery.isError.value) void statusQuery.refetch();
}

watch(isOpen, (open) => {
    if (open && pillState.value.kind === 'unavailable') retry();
});

// Only zones that are behind are listed. Until the status loads nothing is known to
// be behind; if it fails, every changed zone is listed under the error rather than none.
const listedZones = computed(() =>
    statusQuery.isError.value
        ? zones.value
        : zones.value.filter((zone) => isZoneBehind(zone, statusByZoneId.value.get(zone.zoneId)))
);
const isStatusPending = computed(() => zoneIds.value.length > 0 && statusQuery.data.value === undefined && !statusQuery.isError.value);
const popoverSummary = computed(() =>
    feed.data.value === undefined || isStatusPending.value || statusQuery.isError.value
        ? ''
        : syncPopoverSummary(listedZones.value.length, feed.hasNextPage.value)
);

// Listed zones with one or two players show the ids in the row: one search for the whole page.
const rowPlayerLookup = computed(() =>
    zoneIdsNeedingRowPlayers(
        listedZones.value.flatMap((zone) => {
            const status = statusByZoneId.value.get(zone.zoneId);
            return status ? [status] : [];
        })
    )
);
const rowPlayersQuery = useRowPlayerIds(
    // Only while the popover is open: the closed pill needs the status, not the players.
    () => (isOpen.value ? rowPlayerLookup.value.zoneIds : []),
    () => rowPlayerLookup.value.playerCount
);
const rowPlayerIdsByZoneId = computed(() => rowPlayersQuery.data.value ?? new Map<number, number[]>());

// The zone lookup cache batches every zone read in one render into one location_zone search.
function locationForZone(zoneId: number): ZoneLocation | null | undefined {
    const summary = zoneSummaryFor(zoneId);
    if (summary === undefined) return undefined;
    const location = summary?.location;
    return location ? { id: location.id, name: location.display_string } : null;
}
const locationGroups = computed(() => (isOpen.value ? groupZonesByLocation(listedZones.value, locationForZone) : []));

// The popover hangs from the pill and must not reach left of the toolbar's first
// control, the list's search box. The list toolbar has no entity selector any more;
// the search box holds its place at the start of the row. Both are measured on open
// and on every resize while it is open.
const pillButton = ref<HTMLElement | null>(null);
const popoverPlacement = ref<SyncPopoverPlacement>({ align: 'end', width: SYNC_POPOVER_PREFERRED_WIDTH });

function measurePopoverPlacement(): void {
    const pill = pillButton.value;
    if (!pill) return;
    const toolbarStart = document.querySelector('[data-testid="toolbar-search-slot"]');
    popoverPlacement.value = syncPopoverPlacement(
        pill.getBoundingClientRect(),
        toolbarStart ? toolbarStart.getBoundingClientRect().left : null,
        window.innerWidth
    );
}

watch(isOpen, async (open) => {
    if (!open) return;
    await nextTick();
    measurePopoverPlacement();
});
useEventListener(window, 'resize', () => {
    if (isOpen.value) measurePopoverPlacement();
});

// A zone, location or player clicked here opens in the inspector and leaves the search
// page as it was. Only "Show all N players in search" changes the search page.
const { showZonePlayersInSearch } = useShowInSearch();
const { openRecord } = useOpenRecordInInspector();

function openZone(zoneId: number): void {
    isOpen.value = false;
    openRecord('LocationZone', zoneId);
}

function openLocation(locationId: number): void {
    isOpen.value = false;
    openRecord('Location', locationId);
}

function openPlayer(locationZonePlayerId: number): void {
    isOpen.value = false;
    openRecord('LocationZonePlayer', locationZonePlayerId);
}

function showZonePlayers(deliveryZoneIds: number[]): void {
    isOpen.value = false;
    void showZonePlayersInSearch(deliveryZoneIds);
}

const SCOPE_OPTIONS: Array<{ value: SyncScope; label: string }> = [
    { value: 'mine', label: 'Mine' },
    { value: 'everyone', label: 'Everyone' },
];
</script>

<template>
    <Popover v-model:open="isOpen">
        <!-- The popover hangs from this span, not from its trigger. The trigger sits in the
             lazy tooltip, whose first hover swaps in a new pill inside the tooltip's own
             positioning. That new pill registered itself as the tooltip's anchor, not the
             popover's, so the popover kept the old pill. The old pill is off the page and
             measures 0,0, and the popover opened in the window's top-left corner. This span
             is outside the tooltip and is exactly as big as the pill. -->
        <PopoverAnchor as-child>
        <span class="relative inline-flex" data-testid="sync-pill-anchor">
        <LazyTooltip side="bottom">
            <PopoverTrigger as-child>
                <button
                    ref="pillButton"
                    type="button"
                    :class="PILL_CLASS"
                    :aria-label="pillLabel"
                    :data-state-kind="pillState.kind"
                    :data-form="props.form"
                    data-testid="sync-status-button"
                >
                    <Unplug v-if="pillState.kind === 'unavailable'" class="size-4" aria-hidden="true" />
                    <span v-else class="relative inline-flex">
                        <component :is="playersIcon" class="size-4" aria-hidden="true" />
                        <!-- Only while the counts are hidden: the problem signal the red count would have carried. -->
                        <span
                            v-if="showProblemDot"
                            class="bg-destructive-foreground absolute -top-0.5 -right-0.5 size-1.5 rounded-full"
                            aria-hidden="true"
                            data-testid="sync-problem-dot"
                        />
                    </span>
                    <span v-if="showWord" data-testid="sync-word">Sync</span>
                    <span v-if="showCounts && pillState.kind === 'counts'" class="inline-flex items-center gap-1">
                        <span class="text-foreground font-semibold" data-testid="sync-players-behind">{{ pillState.playersBehind }}</span>
                        <span aria-hidden="true">·</span>
                        <span
                            class="font-semibold"
                            :class="pillState.playersNotSent > 0 ? 'text-destructive-foreground' : 'text-muted-foreground'"
                            data-testid="sync-players-not-sent"
                        >
                            {{ pillState.playersNotSent }}
                        </span>
                    </span>
                </button>
            </PopoverTrigger>
            <template #content>{{ pillLabel }}</template>
        </LazyTooltip>
        </span>
        </PopoverAnchor>
        <PopoverContent
            :align="popoverPlacement.align"
            class="p-0"
            :style="{ width: `${popoverPlacement.width}px` }"
            data-testid="sync-popover"
        >
            <!-- One line: a summary that follows the toggle on the left, the toggle on the right. -->
            <div class="flex items-center justify-between gap-3 px-3 pt-2.5 pb-1">
                <span class="text-muted-foreground text-xs" data-testid="sync-summary">{{ popoverSummary }}</span>
                <!-- The search page's type tabs: rounded-lg chips, the selected one tinted. -->
                <div class="flex items-center gap-1" role="radiogroup" aria-label="Whose changes">
                    <button
                        v-for="option in SCOPE_OPTIONS"
                        :key="option.value"
                        type="button"
                        role="radio"
                        :aria-checked="scope === option.value"
                        class="text-muted-foreground hover:text-foreground aria-checked:bg-foreground/[0.06] aria-checked:text-foreground focus-visible:ring-ring h-8 shrink-0 rounded-lg px-3 text-sm focus-visible:outline-none focus-visible:ring-1"
                        :data-testid="`sync-scope-${option.value}`"
                        @click="scope = option.value"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </div>

            <div class="max-h-[60vh] overflow-y-auto px-3 pb-2">
                <p v-if="feed.isLoading.value" class="text-muted-foreground py-6 text-center text-sm">Loading…</p>
                <div v-else-if="feed.isError.value" class="py-6 text-center text-sm" data-testid="sync-error">
                    <p class="text-destructive-foreground">Couldn't load the pending changes.</p>
                    <button type="button" class="text-muted-foreground hover:text-foreground mt-1 text-xs underline" @click="retry">
                        Retry
                    </button>
                </div>
                <p v-else-if="isStatusPending" class="text-muted-foreground py-6 text-center text-sm">Loading…</p>
                <p
                    v-else-if="listedZones.length === 0"
                    class="text-muted-foreground py-6 text-center text-sm"
                    data-testid="sync-empty"
                >
                    Every player is up to date
                </p>
                <template v-else>
                    <p v-if="statusQuery.isError.value" class="text-destructive-foreground pb-1 text-xs">
                        Couldn't load the delivery status.
                        <button type="button" class="text-muted-foreground hover:text-foreground ml-1 underline" @click="retry">
                            Retry
                        </button>
                    </p>
                    <!-- The whole list is one grid: chevron, zone, ids, state. Every row places its
                         cells straight into it (display: contents), so the ids column is as wide as
                         the widest ids on the page and the zone name gets all the rest. Headings and
                         details span every column. -->
                    <div
                        class="grid grid-cols-[auto_minmax(0,1fr)_max-content_max-content] gap-x-3"
                        data-testid="sync-zone-grid"
                    >
                        <template v-for="group in locationGroups" :key="group.key">
                            <h5
                                class="text-muted-foreground col-span-full truncate pt-1.5 pb-0.5 text-xs"
                                data-testid="sync-location-group"
                            >
                                <button
                                    v-if="group.location"
                                    type="button"
                                    class="hover:underline focus-visible:outline-none focus-visible:underline"
                                    data-testid="sync-location-link"
                                    @click="openLocation(group.location.id)"
                                >
                                    {{ group.heading }}
                                </button>
                                <span v-else>{{ group.heading }}</span>
                                <span v-if="group.accountName" class="text-muted-foreground/70"> · {{ group.accountName }}</span>
                            </h5>
                            <SyncZoneRow
                                v-for="zone in group.zones"
                                :key="zone.zoneId"
                                :zone="zone"
                                :status="statusByZoneId.get(zone.zoneId)"
                                :status-loading="statusQuery.isLoading.value"
                                :row-player-ids="rowPlayerIds(statusByZoneId.get(zone.zoneId), rowPlayerIdsByZoneId)"
                                @open-zone="openZone"
                                @open-player="openPlayer"
                                @show-zone-players="showZonePlayers"
                            />
                        </template>
                    </div>
                </template>
            </div>

            <div v-if="feed.hasNextPage.value" class="border-border border-t p-1">
                <button
                    type="button"
                    class="text-muted-foreground hover:text-foreground hover:bg-muted/60 w-full rounded py-1.5 text-xs disabled:opacity-50"
                    :disabled="feed.isFetchingNextPage.value"
                    data-testid="sync-show-older"
                    @click="feed.fetchNextPage()"
                >
                    {{ feed.isFetchingNextPage.value ? 'Loading…' : 'Show older changes' }}
                </button>
            </div>
        </PopoverContent>
    </Popover>
</template>
